import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi, useApiClient } from '../contexts/ApiContext';
import {
  ApiChallengeCompleted,
  ApiConfirmEmailRequest,
  ApiConfirmPhoneRequest,
  ApiSetEmailRequest,
  ApiSetPasswordRequest,
  ApiSetPhoneRequest,
  ApiUpdateEmailRequest,
  ApiUpdatePhoneRequest,
} from '../openapi';
import { addSeconds } from 'date-fns';
import { mapResponse } from './helpers';

export function useSetPasswordMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'set-password', idempotencyKey],
    mutationFn: (body: ApiSetPasswordRequest) =>
      api
        .POST('/businesses/current/password/set', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useUpdatePasswordMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'password', 'update', idempotencyKey],
    mutationFn: async (body: {
      currentPassword: string;
      newPassword: string;
    }): Promise<ApiChallengeCompleted> => {
      let flow = await api.client.POST('/businesses/current/password/update', {
        body: {},
        headers: { 'Idempotency-Key': idempotencyKey },
      });

      if (
        flow.data &&
        flow.data.type === 'CHALLENGE_REQUIRED' &&
        flow.data.challenge === 'PASSWORD'
      ) {
        flow = await api.client.POST('/businesses/current/password/update', {
          body: { solution: body.currentPassword },
          headers: { 'Idempotency-Key': idempotencyKey },
        });
      }

      if (flow.data && flow.data.type === 'CHALLENGES_COMPLETED') {
        flow = await api.client.POST('/businesses/current/password/update', {
          body: { password: body.newPassword },
          headers: { 'Idempotency-Key': idempotencyKey },
        });
      }

      if (
        flow.data &&
        typeof flow.data.accessToken === 'string' &&
        typeof flow.data.refreshToken === 'string'
      ) {
        return {
          accessToken: flow.data.accessToken,
          refreshToken: flow.data.refreshToken,
          type: 'CHALLENGES_COMPLETED',
        };
      } else {
        throw new Error('Unexpected response');
      }
    },
    onSuccess: (data) => {
      if (data.accessToken && data.refreshToken) {
        api.setAccessToken(data.accessToken);
        api.setRefreshToken(data.refreshToken);
      }

      queryClient.invalidateQueries();
    },
  });
}

export function useSetPhoneMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'set-phone', idempotencyKey],
    mutationFn: (body: ApiSetPhoneRequest) =>
      api
        .POST('/businesses/current/phone/set', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then((res) => addSeconds(new Date(), res.data?.resendInSec ?? 0)),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useUpdatePhoneMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'update-phone', idempotencyKey],
    mutationFn: (body: ApiUpdatePhoneRequest) =>
      api
        .POST('/businesses/current/phone/update', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(() => null),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useConfirmPhoneMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'confirm-phone', idempotencyKey],
    mutationFn: (body: ApiConfirmPhoneRequest) =>
      api
        .POST('/businesses/current/phone/confirm', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}

export function useSetEmailMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'set-email', idempotencyKey],
    mutationFn: (body: ApiSetEmailRequest) =>
      api
        .POST('/businesses/current/email/set', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then((res) => addSeconds(new Date(), res.data?.resendInSec ?? 0)),
  });
}

export function useUpdateEmailMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'update-email', idempotencyKey],
    mutationFn: (body: ApiUpdateEmailRequest) =>
      api
        .POST('/businesses/current/email/update', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(() => null),
  });
}

export function useConfirmEmailMutation({ idempotencyKey }: { idempotencyKey: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['businesses', 'current', 'confirm-email', idempotencyKey],
    mutationFn: (body: ApiConfirmEmailRequest) =>
      api
        .POST('/businesses/current/email/confirm', {
          body,
          headers: { 'Idempotency-Key': idempotencyKey },
        })
        .then(mapResponse),
    onSuccess: () => queryClient.invalidateQueries(),
  });
}
